import { zodResolver } from '@hookform/resolvers/zod';
import {
  loginRequest,
  type LoginChallengeResponse,
  type MfaEnrollStartResponse,
  type SessionResponse,
  z,
} from '@zenarty/shared';
import { ArrowLeft, Boxes, Copy, FileCheck2, ShieldCheck, Truck } from 'lucide-react';
import QRCode from 'qrcode';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { api } from '../api';
import { useAuth } from '../auth';
import { Button, Callout, ErrorMessage, Field } from '../components/kit';
import { LogoTile } from '../components/logo';

type Step =
  | { kind: 'credentials' }
  | { kind: 'enroll'; challenge: string }
  | { kind: 'verify'; challenge: string }
  | { kind: 'recovery'; session: SessionResponse };

export function LoginPage() {
  const [step, setStep] = useState<Step>({ kind: 'credentials' });
  return (
    <div className="auth">
      <aside className="auth-aside">
        <div className="brand" style={{ padding: 0 }}>
          <span className="brand-mark">
            <LogoTile size={32} />
          </span>
          <div>
            <div className="brand-name">Zenarty WMS</div>
            <div className="brand-sub">Warehouse management for 3PLs</div>
          </div>
        </div>
        <div>
          <h2>Every pallet, every client, every retailer rule — in one place.</h2>
          <p className="lead">
            Run receiving, inventory and shipping across all your sites, with each client's goods
            kept strictly separate.
          </p>
          <ul>
            <li>
              <Boxes />
              <span>
                <strong>Multi-client inventory</strong>Real-time stock by site, location, lot and
                client.
              </span>
            </li>
            <li>
              <Truck />
              <span>
                <strong>Retailer-ready shipping</strong>Labels and ASNs that match, so chargebacks
                don't happen.
              </span>
            </li>
            <li>
              <FileCheck2 />
              <span>
                <strong>Billing from real activity</strong>Every receipt, pick and pallet day
                captured automatically.
              </span>
            </li>
            <li>
              <ShieldCheck />
              <span>
                <strong>Secure by design</strong>Two-step sign-in, tenant isolation in the database,
                full audit trail.
              </span>
            </li>
          </ul>
        </div>
        <div className="brand-sub">© {new Date().getFullYear()} Zenarty</div>
      </aside>
      <main className="auth-main">
        <div className="auth-card">
          {step.kind === 'credentials' ? (
            <Credentials
              onChallenge={(r) =>
                setStep(
                  r.status === 'mfa_enroll_required'
                    ? { kind: 'enroll', challenge: r.challengeToken }
                    : { kind: 'verify', challenge: r.challengeToken },
                )
              }
            />
          ) : null}
          {step.kind === 'enroll' ? (
            <Enroll
              challenge={step.challenge}
              onDone={(session) => setStep({ kind: 'recovery', session })}
              onBack={() => setStep({ kind: 'credentials' })}
            />
          ) : null}
          {step.kind === 'verify' ? (
            <Verify challenge={step.challenge} onBack={() => setStep({ kind: 'credentials' })} />
          ) : null}
          {step.kind === 'recovery' ? <RecoveryCodes session={step.session} /> : null}
        </div>
      </main>
    </div>
  );
}

function Credentials({ onChallenge }: { onChallenge: (r: LoginChallengeResponse) => void }) {
  const { establish } = useAuth();
  const [error, setError] = useState<unknown>(null);
  const form = useForm<z.input<typeof loginRequest>, unknown, z.output<typeof loginRequest>>({
    resolver: zodResolver(loginRequest),
  });
  const submit = form.handleSubmit(async (values) => {
    setError(null);
    try {
      const r = await api<LoginChallengeResponse | SessionResponse>('/auth/login', {
        method: 'POST',
        body: values,
      });
      if (r.status === 'authenticated') await establish(r);
      else onChallenge(r);
    } catch (e) {
      setError(e);
    }
  });
  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <div>
        <h1>Sign in</h1>
        <p className="desc">
          Use your work email. If you have set up two-step sign-in, you'll confirm with your
          authenticator app next.
        </p>
      </div>
      <ErrorMessage error={error} />
      <Field label="Email" error={form.formState.errors.email?.message}>
        <input
          className="input"
          type="email"
          autoComplete="username"
          autoFocus
          {...form.register('email')}
        />
      </Field>
      <Field label="Password" error={form.formState.errors.password?.message}>
        <input
          className="input"
          type="password"
          autoComplete="current-password"
          {...form.register('password')}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="btn-block"
        loading={form.formState.isSubmitting}
      >
        Continue
      </Button>
      <p className="small muted">Floor staff sign in on their scanner with badge and PIN.</p>
    </form>
  );
}

const codeSchema = z.object({ code: z.string().regex(/^\d{6}$/, 'Enter the 6-digit code') });

function Enroll({
  challenge,
  onDone,
  onBack,
}: {
  challenge: string;
  onDone: (s: SessionResponse) => void;
  onBack: () => void;
}) {
  const [setup, setSetup] = useState<(MfaEnrollStartResponse & { qr: string }) | null>(null);
  const [error, setError] = useState<unknown>(null);
  const form = useForm<{ code: string }>({ resolver: zodResolver(codeSchema) });

  useEffect(() => {
    void (async () => {
      try {
        const r = await api<MfaEnrollStartResponse>('/auth/mfa/enroll/start', {
          method: 'POST',
          token: challenge,
        });
        setSetup({ ...r, qr: await QRCode.toDataURL(r.otpauthUrl, { margin: 1, width: 200 }) });
      } catch (e) {
        setError(e);
      }
    })();
  }, [challenge]);

  const submit = form.handleSubmit(async ({ code }) => {
    setError(null);
    try {
      onDone(
        await api<SessionResponse>('/auth/mfa/enroll/confirm', {
          method: 'POST',
          token: challenge,
          body: { code },
        }),
      );
    } catch (e) {
      setError(e);
    }
  });

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <div>
        <h1>Set up two-step sign-in</h1>
        <p className="desc">
          Scan this code with an authenticator app (Google Authenticator, Microsoft Authenticator,
          1Password…), then enter the 6-digit code it shows.
        </p>
      </div>
      <ErrorMessage error={error} />
      <div className="qr">
        {setup ? (
          <img src={setup.qr} alt="Authenticator QR code" width={200} height={200} />
        ) : (
          <div className="spinner" />
        )}
        {setup ? (
          <details>
            <summary className="small muted" style={{ cursor: 'pointer' }}>
              Can't scan? Enter this key instead
            </summary>
            <code className="secret" style={{ marginTop: 8 }}>
              {setup.secret}
            </code>
          </details>
        ) : null}
      </div>
      <Field label="6-digit code" error={form.formState.errors.code?.message}>
        <input
          className="input mono"
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={6}
          {...form.register('code')}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="btn-block"
        disabled={!setup}
        loading={form.formState.isSubmitting}
      >
        Verify and continue
      </Button>
      <Button variant="ghost" icon={ArrowLeft} onClick={onBack}>
        Back
      </Button>
    </form>
  );
}

function Verify({ challenge, onBack }: { challenge: string; onBack: () => void }) {
  const { establish } = useAuth();
  const [useRecovery, setUseRecovery] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const form = useForm<{ value: string }>();

  const submit = form.handleSubmit(async ({ value }) => {
    setError(null);
    try {
      const body = useRecovery ? { recoveryCode: value.trim() } : { code: value.trim() };
      await establish(
        await api<SessionResponse>('/auth/mfa/verify', { method: 'POST', token: challenge, body }),
      );
    } catch (e) {
      setError(e);
    }
  });

  return (
    <form onSubmit={(e) => void submit(e)} noValidate>
      <div>
        <h1>Two-step verification</h1>
        <p className="desc">
          {useRecovery
            ? 'Enter one of the recovery codes you saved. Each works once.'
            : 'Open your authenticator app and enter the current code.'}
        </p>
      </div>
      <ErrorMessage error={error} />
      <Field label={useRecovery ? 'Recovery code' : '6-digit code'}>
        <input
          className="input mono"
          autoFocus
          autoComplete="one-time-code"
          inputMode={useRecovery ? 'text' : 'numeric'}
          {...form.register('value', { required: true })}
        />
      </Field>
      <Button
        type="submit"
        variant="primary"
        size="lg"
        className="btn-block"
        loading={form.formState.isSubmitting}
      >
        Sign in
      </Button>
      <div className="row" style={{ justifyContent: 'space-between' }}>
        <button type="button" className="btn-link" onClick={onBack}>
          ← Different account
        </button>
        <button type="button" className="btn-link" onClick={() => setUseRecovery(!useRecovery)}>
          {useRecovery ? 'Use authenticator code' : 'Lost your phone? Use a recovery code'}
        </button>
      </div>
    </form>
  );
}

function RecoveryCodes({ session }: { session: SessionResponse }) {
  const { establish } = useAuth();
  const codes = session.recoveryCodes ?? [];
  return (
    <div className="stack">
      <div>
        <h1>Save your recovery codes</h1>
        <p className="desc">If you lose your phone, each code lets you sign in once.</p>
      </div>
      <Callout tone="warn" title="These are shown only once">
        Store them in your password manager or print them. Zenarty staff can't see them.
      </Callout>
      <ul className="recovery-codes">
        {codes.map((c) => (
          <li key={c}>{c}</li>
        ))}
      </ul>
      <Button icon={Copy} onClick={() => void navigator.clipboard?.writeText(codes.join('\n'))}>
        Copy all codes
      </Button>
      <Button
        variant="primary"
        size="lg"
        className="btn-block"
        onClick={() => void establish(session)}
      >
        I've saved them — continue
      </Button>
    </div>
  );
}
